import React, { useMemo, useState } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  addMinutes,
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  parse,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { batchData } from "../data/batchData";
import CalendarToolbar from "./CalendarToolbar";
import MonthView from "./MonthView";
import WeekView from "./WeekView";
import DayView from "./DayView";
import BatchDetailModal from "./BatchDetailModal";

const parseMinutes = (timeStr) => {
  const parsed = parse(String(timeStr || "00:00"), "HH:mm", new Date());
  return parsed.getHours() * 60 + parsed.getMinutes();
};

const INSTITUTE_START_HOUR = 6;
const INSTITUTE_END_HOUR = 21;
const INSTITUTE_START_MIN = INSTITUTE_START_HOUR * 60;
const INSTITUTE_END_MIN = INSTITUTE_END_HOUR * 60;
const SLOT_MINUTES = 30;
const instituteSlots = Array.from(
  { length: (INSTITUTE_END_MIN - INSTITUTE_START_MIN) / SLOT_MINUTES },
  (_, i) => {
    const startMinutes = INSTITUTE_START_MIN + i * SLOT_MINUTES;
    const endMinutes = startMinutes + SLOT_MINUTES;
    const startDate = addMinutes(new Date().setHours(0, 0, 0, 0), startMinutes);
    const endDate = addMinutes(new Date().setHours(0, 0, 0, 0), endMinutes);
    return {
      key: `${startMinutes}-${endMinutes}`,
      startMinutes,
      endMinutes,
      label: `${format(startDate, "h:mm a")} - ${format(endDate, "h:mm a")}`,
    };
  }
);

const BatchCalendar = () => {
  const navigate = useNavigate();
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [filterBatch, setFilterBatch] = useState("all");
  const [selectedBatch, setSelectedBatch] = useState(null);

  const filteredBatches = useMemo(() => {
    if (filterBatch === "all") return batchData;
    return batchData.filter((b) => b.id === filterBatch);
  }, [filterBatch]);

  const eventsByDate = useMemo(() => {
    const monthStart = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
    const monthEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const map = {};
    days.forEach((d) => {
      const key = d.toISOString().slice(0, 10);
      map[key] = filteredBatches.filter((b) => b.days.includes(d.getDay()));
    });
    return map;
  }, [cursor, filteredBatches]);

  const getEventsForDayAndSlot = (date, slot) =>
    filteredBatches.filter((b) => {
      if (!b.days.includes(date.getDay())) return false;
      const startMinutes = parseMinutes(b.startTime ?? b.time);
      let endMinutes = parseMinutes(b.endTime ?? b.time);
      if (!Number.isFinite(endMinutes) || endMinutes <= startMinutes) {
        endMinutes = startMinutes + 60;
      }
      return startMinutes < slot.endMinutes && endMinutes > slot.startMinutes;
    });
  const instituteTimingLabel = `${format(
    new Date().setHours(INSTITUTE_START_HOUR, 0, 0, 0),
    "h a"
  )} - ${format(new Date().setHours(INSTITUTE_END_HOUR, 0, 0, 0), "h a")}`;

  const onPrev = () => {
    if (view === "month") setCursor((d) => subMonths(d, 1));
    else if (view === "week") setCursor((d) => subWeeks(d, 1));
    else setCursor((d) => subDays(d, 1));
  };

  const onNext = () => {
    if (view === "month") setCursor((d) => addMonths(d, 1));
    else if (view === "week") setCursor((d) => addWeeks(d, 1));
    else setCursor((d) => addDays(d, 1));
  };

  const onToday = () => setCursor(new Date());
  const handleViewFullBatch = (batch) => {
    const rawId =
      batch?.organization_ems_batch_id ??
      batch?.batch_id ??
      batch?.id ??
      "";
    const id = String(rawId).trim();
    setSelectedBatch(null);
    if (/^\d+$/.test(id)) {
      navigate(`/batches/view/${id}`);
      return;
    }
    navigate("/batches");
  };

  const periodLabel = useMemo(() => {
    if (view === "month") return format(cursor, "MMMM yyyy");
    if (view === "week") {
      const weekStart = startOfWeek(cursor, { weekStartsOn: 0 });
      const weekEnd = addDays(weekStart, 6);
      return `${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")}`;
    }
    return format(cursor, "EEEE, d MMMM yyyy");
  }, [cursor, view]);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Paper variant="outlined" elevation={0} sx={{ borderRadius: 3, p: { xs: 1, sm: 1.5 } }}>
        <CalendarToolbar
          view={view}
          onViewChange={setView}
          onPrev={onPrev}
          onNext={onNext}
          onToday={onToday}
          periodLabel={periodLabel}
          filterBatch={filterBatch}
          onFilterBatchChange={setFilterBatch}
          batches={batchData}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Institute timing: {instituteTimingLabel}
        </Typography>

        {view === "month" ? (
          <MonthView cursor={cursor} eventsByDate={eventsByDate} onBatchClick={setSelectedBatch} />
        ) : null}

        {view === "week" ? (
          <WeekView
            cursor={cursor}
            getEventsForDayAndSlot={getEventsForDayAndSlot}
            onBatchClick={setSelectedBatch}
            slots={instituteSlots}
          />
        ) : null}

        {view === "day" ? (
          <DayView
            cursor={cursor}
            getEventsForDayAndSlot={getEventsForDayAndSlot}
            onBatchClick={setSelectedBatch}
            slots={instituteSlots}
            instituteTimingLabel={instituteTimingLabel}
          />
        ) : null}

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
          {filteredBatches.map((b) => (
            <Stack key={b.id} direction="row" alignItems="center" gap={0.5}>
              <Box
                sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: b.color.bg }}
              />
              <Typography variant="caption" color="text.secondary">
                {b.label}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Paper>

      <BatchDetailModal
        batch={selectedBatch}
        open={Boolean(selectedBatch)}
        onClose={() => setSelectedBatch(null)}
        onViewFullBatch={handleViewFullBatch}
      />
    </Box>
  );
};

export default BatchCalendar;

